<template>
	<Col class="width-100 pt-2">
		<Row>
			<h3>Ply URL</h3>
		</Row>
		<Row>
			<LinkCopy :src="plyLink" class="width-100" />
		</Row>

		<template v-if="isPortal && isCustomDomainsEnabled">
			<MaybeUpgradeRequiredIndicator :billingFeatureId="billingFeatureId">
				<template #default="{ indicator, isUpgradeRequired }">
					<Row class="flex-justify-space-between mt-5 mr-1">
						<h3>Custom URL</h3>
						<Row class="flex-justify-space-between">
							<a class="fs-14 purple-text" @click="(e) => editCustomURL(e, isUpgradeRequired, indicator)" href="#"
								>{{ customURLExists ? 'Edit' : 'Set Custom' }} URL</a
							>
							<div class="pl-2" v-if="billingFeatureId && isUpgradeRequired">
								<component :is="indicator" :billingFeatureId="billingFeatureId" iconOnly />
							</div>
						</Row>
					</Row>
				</template>
			</MaybeUpgradeRequiredIndicator>
			<Row v-if="customURLExists">
				<LinkCopy :src="customURL" class="width-100" />
			</Row>
		</template>

		<div>
			<ParamOptionSettings style="width: 102%" @updateOptions="updateOptions" />
		</div>
		<div v-if="isPortal && customerAuthSettings.customer_auth_enabled">
			<h3>Sample customer email</h3>
			<AppTextField v-model="sampleCustomerEmail" placeholder="Enter sample customer email" outlined />
		</div>
	</Col>
</template>

<script>
import debounce from 'lodash/debounce';

import ParamOptionSettings from '../ParamOptionSettings.vue';
import MaybeUpgradeRequiredIndicator from '@/components/Billing/MaybeUpgradeRequiredIndicator.vue';
import { FeatureTypes } from '@/components/Billing/billing-types';
import LinkCopy from '@/components/common/LinkCopy/LinkCopy.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { isCustomDomainsEnabled } from '@/store/modules/featureFlags/getters';
import { saveTriggerUrl } from '@/store/modules/selectedWorkflow/actions';
import { isPortal, customerAuthSettings, editedWorkflowVersion } from '@/store/modules/selectedWorkflow/getters';
import { updateWorkflowVersion } from '@/store/modules/workflows/modules/versions/actions.js';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import { DEFAULT_SAMPLE_CUSTOMER_EMAIL } from '@/utils/WorkflowService';

export default {
	name: 'URLToolPlacementEditor',
	props: { workflow: { type: Object } },
	components: { ParamOptionSettings, LinkCopy, MaybeUpgradeRequiredIndicator },
	computed: {
		...mapGettersShorthand({ isCustomDomainsEnabled, isPortal, customerAuthSettings, editedWorkflowVersion }),
		billingFeatureId() {
			return FeatureTypes.CustomerCustomDomains;
		},
		plyLink() {
			const base = this.workflow.ply_run_link;
			const url = new URL(base);
			this.urlParamsLabels.forEach((param) => url.searchParams.set(param, this.urlParams[param] || 'xxxx'));
			this.saveTriggerUrl(url.href);
			return url.href;
		},
		sampleCustomerEmail: {
			get() {
				return this.editedWorkflowVersion.sample_data?.customer?.email || DEFAULT_SAMPLE_CUSTOMER_EMAIL;
			},
			set: debounce(async function (value) {
				this.updateWorkflowVersion({
					workflowId: this.workflow.id,
					workflowVersionId: this.workflow.current_draft.id,
					partialVersionObject: { sample_data: { customer: { email: value } } },
				});
			}, 1000),
		},
		customURL() {
			return this.workflow.run_link;
		},
		customURLExists() {
			return !!this.workflow.tool_preferences.custom_domain;
		},
	},
	methods: {
		...mapActionsShorthand({ saveTriggerUrl, updateWorkflowVersion }),
		copyText(text) {
			navigator.clipboard.writeText(text);
		},
		updateOptions(dropdownOptions) {
			this.urlParamsLabels = dropdownOptions.map((x) => x.text);
			this.urlParams = dropdownOptions.reduce((params, x) => ({ ...params, ...{ [x.text]: x.value } }), {});
		},
		editCustomURL(event, isUpgradeRequired, indicator) {
			if (isUpgradeRequired && indicator?.setup) {
				indicator.setup().openUpgrade(event, this.billingFeatureId);
			} else {
				AppDialogManagerService.openSetCustomDomainForWorkflowDialog({ workflow: this.workflow, plyLink: this.plyLink });
			}
		},
	},
	data() {
		return { urlParamsLabels: [], urlParams: {} };
	},
};
</script>

<style lang="scss" scoped>
.fs-14 {
	font-size: 14px;
}
.purple-text {
	color: #6162d8;
}
</style>
